/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  BulkProductsRequest,
  BulkResponse,
  DeleteAdminV1OrgProductsIdParams,
  DeleteAdminV1OrgProductsIdPathParameters,
  DuplicateProductRequest,
  GetAdminV1OrgProductsIdMedia200,
  GetAdminV1OrgProductsIdMediaPathParameters,
  GetAdminV1OrgProductsIdPathParameters,
  GetAdminV1OrgProductsIdRelations200,
  GetAdminV1OrgProductsIdRelationsPathParameters,
  GetAdminV1OrgProductsIdSkuHistory200,
  GetAdminV1OrgProductsIdSkuHistoryPathParameters,
  GetAdminV1OrgProductsIdSkuPreviewParams,
  GetAdminV1OrgProductsIdSkuPreviewPathParameters,
  GetAdminV1OrgProductsParams,
  GetAdminV1OrgProductsTagsParams,
  GetAdminV1OrgProductsVendorsParams,
  PatchAdminV1OrgProductsIdBody,
  PatchAdminV1OrgProductsIdOptionsReorderPathParameters,
  PatchAdminV1OrgProductsIdPathParameters,
  PostAdminV1OrgProductsBody,
  PostAdminV1OrgProductsIdDuplicatePathParameters,
  PostAdminV1OrgProductsIdRegenerateSkusPathParameters,
  Product,
  ProductFacetList,
  ProductHealth,
  ProductListPage,
  ProductLookupRequest,
  ProductLookupResponse,
  ProductSaveResult,
  ProductSkuPreview,
  PutAdminV1OrgProductsIdMedia200,
  PutAdminV1OrgProductsIdMediaBody,
  PutAdminV1OrgProductsIdMediaPathParameters,
  PutAdminV1OrgProductsIdRelations200,
  PutAdminV1OrgProductsIdRelationsBody,
  PutAdminV1OrgProductsIdRelationsPathParameters,
  RegenerateSkusRequest,
  RegenerateSkusResponse,
  ReorderProductOptionsRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Filters (D-182) combine with AND, values of one filter with OR. `attr.<key>=a,b` filters a filterable select, multiselect or boolean attribute by value code (or true/false); an unknown or non-filterable key answers 422 on query.attr.<key>. `includeTotal=true` on the first page adds `total` (at most 10,000) and `totalCapped`. `memberOf=store:<id>|collection:<id>|schedule:<id>` adds `isMember` and `memberReason` to each row; a target the caller cannot see answers 404 on query.memberOf. Sorts take the id as tie-break; `price` is the lowest active variant's base price, products without one last. A filter of the other list's scope answers 422
 * @summary List products of the organization, newest first
 */
export const getAdminV1OrgProducts = (
    params?: GetAdminV1OrgProductsParams,
 options?: SecondParameter<typeof customInstance<ProductListPage>>,) => {
      return customInstance<ProductListPage>(
      {url: `/admin/v1/org/products`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Options are the variant axes; pass them in display order. Variants can be sent here, generated from the options with variantSync, or generated or added separately. An active product must carry every attribute its product type marks as required
 * @summary Create a product with its attribute values, option axes and variants
 */
export const postAdminV1OrgProducts = (
    postAdminV1OrgProductsBody: BodyType<PostAdminV1OrgProductsBody>,
 options?: SecondParameter<typeof customInstance<ProductSaveResult>>,) => {
      return customInstance<ProductSaveResult>(
      {url: `/admin/v1/org/products`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1OrgProductsBody
    },
      options);
    }
  /**
 * @summary Counts behind the product list's health tiles
 */
export const getAdminV1OrgProductsHealth = (

 options?: SecondParameter<typeof customInstance<ProductHealth>>,) => {
      return customInstance<ProductHealth>(
      {url: `/admin/v1/org/products/health`, method: 'GET'
    },
      options);
    }
  /**
 * Exact and case-insensitive, up to 5,000 values. `auto` tries a product or variant id, then a variant SKU, then a barcode, then a handle, and keeps the first kind that matched each value. Deleted products and variants never match. Read-only; the picker adds the matches to its selection and lists notFound
 * @summary Find products by pasted ids, SKUs, barcodes or handles
 */
export const postAdminV1OrgProductsLookup = (
    productLookupRequest: BodyType<ProductLookupRequest>,
 options?: SecondParameter<typeof customInstance<ProductLookupResponse>>,) => {
      return customInstance<ProductLookupResponse>(
      {url: `/admin/v1/org/products/lookup`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: productLookupRequest
    },
      options);
    }
  /**
 * @summary Distinct product tags of the organization, most used first
 */
export const getAdminV1OrgProductsTags = (
    params?: GetAdminV1OrgProductsTagsParams,
 options?: SecondParameter<typeof customInstance<ProductFacetList>>,) => {
      return customInstance<ProductFacetList>(
      {url: `/admin/v1/org/products/tags`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Distinct product vendors of the organization, most used first
 */
export const getAdminV1OrgProductsVendors = (
    params?: GetAdminV1OrgProductsVendorsParams,
 options?: SecondParameter<typeof customInstance<ProductFacetList>>,) => {
      return customInstance<ProductFacetList>(
      {url: `/admin/v1/org/products/vendors`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Get a product with its attribute values and options
 */
export const getAdminV1OrgProductsId = (
    { id }: GetAdminV1OrgProductsIdPathParameters,
 options?: SecondParameter<typeof customInstance<Product>>,) => {
      return customInstance<Product>(
      {url: `/admin/v1/org/products/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * With variants in the body the options and the variant set are saved together: listed variants are created or updated, the rest are deleted, and one that is published, stocked or on an order or cart line answers 409 RESOURCE_IN_USE with meta.variantIds. Without variants, options can change while variants exist only if every active variant still fits the new axes. Otherwise 409 with meta.variants ({ id, title, sku, reason: missing_option | removed_value }, first 20) and meta.count, unless unfitVariants is archive: then those variants are archived in the same transaction. With variantSync the variants are fitted to the options instead, without that 409, and the answer carries variantSync { created, archived }
 * @summary Update a product. attributeValues, options and variants replace the whole list when passed
 */
export const patchAdminV1OrgProductsId = (
    { id }: PatchAdminV1OrgProductsIdPathParameters,
    patchAdminV1OrgProductsIdBody: BodyType<PatchAdminV1OrgProductsIdBody>,
 options?: SecondParameter<typeof customInstance<ProductSaveResult>>,) => {
      return customInstance<ProductSaveResult>(
      {url: `/admin/v1/org/products/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1OrgProductsIdBody
    },
      options);
    }
  /**
 * 409 RESOURCE_IN_USE with meta.storeIds while any store carries the product, whatever its status there. With removeFromStores=true every store row goes in the same transaction, as DELETE of each store product would remove it, which needs products.write in each of those stores, and with them every store price and market price of the product's variants, archived ones included, in any store; refused with 409 RESOURCE_IN_USE and meta { reason: auction | rotation, storeIds } while a store has an auction of the product (the auction row keeps it in the store, also once ended) or a rotation holds a row that has been published. A draft a rotation put there and nothing ever published goes with the product
 * @summary Delete a product, optionally removing it from every store first
 */
export const deleteAdminV1OrgProductsId = (
    { id }: DeleteAdminV1OrgProductsIdPathParameters,
    params?: DeleteAdminV1OrgProductsIdParams,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/products/${id}`, method: 'DELETE',
        params
    },
      options);
    }
  /**
 * Copies fields, attribute values, options and, when asked, media and variants. The copy is a draft in no store; its SKU rule allocates a fresh style number and variant SKUs, without a rule it has none
 * @summary Copy a product into a new draft
 */
export const postAdminV1OrgProductsIdDuplicate = (
    { id }: PostAdminV1OrgProductsIdDuplicatePathParameters,
    duplicateProductRequest: BodyType<DuplicateProductRequest>,
 options?: SecondParameter<typeof customInstance<Product>>,) => {
      return customInstance<Product>(
      {url: `/admin/v1/org/products/${id}/duplicate`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: duplicateProductRequest
    },
      options);
    }
  /**
 * set_status, add_tags, remove_tags, delete, publish (into every storeIds), schedule or clear_schedule, on ids (at most 5,000) or an org-scope selection, exactly one of them (422 INVALID_INPUT). Each product is checked on its own and answered with the status a single request would have had; 200 when all succeeded, 207 otherwise. An id list answers every id; a selection answers its failures only and adds matched. A selection resolves inside the call: 409 SELECTION_CHANGED (meta.expected, meta.actual) when its filter no longer counts expectedCount, 422 SELECTION_TOO_LARGE (meta.limit) past 5,000 products. Unchanged products (status or tags already as asked) succeed without a write. schedule writes publishAt and/or unpublishAt (future, unpublish after publish; a date left out keeps the stored one) on the store product in every storeIds, creating a missing one as a draft with the master handle; per id 404 unknown, 409 CONFLICT for publishAt on a master that is not active, 409 ALREADY_EXISTS (meta.storeId, meta.handle) when the handle is taken in a store, 422 SCHEDULE_CONFLICT (meta.scheduleId) when a rotation owns the product in one of the stores, 422 INVALID_INPUT when a stored date would fall on the wrong side; a failed id is changed in none of the stores. clear_schedule clears both dates in every storeIds, rotation-owned rows included. Both need access to every store (404 with field body.storeIds.N otherwise)
 * @summary Apply one action to many products
 */
export const postAdminV1OrgProductsBulk = (
    bulkProductsRequest: BodyType<BulkProductsRequest>,
 options?: SecondParameter<typeof customInstance<BulkResponse>>,) => {
      return customInstance<BulkResponse>(
      {url: `/admin/v1/org/products/bulk`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: bulkProductsRequest
    },
      options);
    }
  /**
 * Every option and every value must be listed; positions follow the order sent. Variants and their option values are untouched
 * @summary Reorder the option axes and their values
 */
export const patchAdminV1OrgProductsIdOptionsReorder = (
    { id }: PatchAdminV1OrgProductsIdOptionsReorderPathParameters,
    reorderProductOptionsRequest: BodyType<ReorderProductOptionsRequest>,
 options?: SecondParameter<typeof customInstance<Product>>,) => {
      return customInstance<Product>(
      {url: `/admin/v1/org/products/${id}/options/reorder`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: reorderProductOptionsRequest
    },
      options);
    }
  /**
 * Per row: current, next and a state. unchanged, change, manual (skipped unless includeManual), inUse (always skipped: the variant is on an order, in a cart or has stock movements; inUseReason says which), conflict (the next value is taken or retired and gets a -N suffix), none (the rule writes nothing for it). The root says whether applying would consume a counter number (D-161)
 * @summary What regenerating this product's style number and SKUs would write
 */
export const getAdminV1OrgProductsIdSkuPreview = (
    { id }: GetAdminV1OrgProductsIdSkuPreviewPathParameters,
    params?: GetAdminV1OrgProductsIdSkuPreviewParams,
 options?: SecondParameter<typeof customInstance<ProductSkuPreview>>,) => {
      return customInstance<ProductSkuPreview>(
      {url: `/admin/v1/org/products/${id}/sku-preview`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Writes what the preview shows for the chosen rows in one transaction: replaced values are retired into the SKU history and never reused, in-use variants are never touched, manual values only with includeManual. A product that already has a sequence keeps its number; one without gets the next
 * @summary Rewrite the style number and variant SKUs from the SKU rule
 */
export const postAdminV1OrgProductsIdRegenerateSkus = (
    { id }: PostAdminV1OrgProductsIdRegenerateSkusPathParameters,
    regenerateSkusRequest: BodyType<RegenerateSkusRequest>,
 options?: SecondParameter<typeof customInstance<RegenerateSkusResponse>>,) => {
      return customInstance<RegenerateSkusResponse>(
      {url: `/admin/v1/org/products/${id}/regenerate-skus`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: regenerateSkusRequest
    },
      options);
    }
  /**
 * Newest first, at most 200. A retired value is never handed out again; imports still resolve it to the variant that carried it
 * @summary Style numbers and SKUs this product and its variants stopped carrying
 */
export const getAdminV1OrgProductsIdSkuHistory = (
    { id }: GetAdminV1OrgProductsIdSkuHistoryPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgProductsIdSkuHistory200>>,) => {
      return customInstance<GetAdminV1OrgProductsIdSkuHistory200>(
      {url: `/admin/v1/org/products/${id}/sku-history`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Media attached to a product, in display order
 */
export const getAdminV1OrgProductsIdMedia = (
    { id }: GetAdminV1OrgProductsIdMediaPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgProductsIdMedia200>>,) => {
      return customInstance<GetAdminV1OrgProductsIdMedia200>(
      {url: `/admin/v1/org/products/${id}/media`, method: 'GET'
    },
      options);
    }
  /**
 * Items are stored in the given order. An item with variantIds shows only for those variants. featuredMediaId defaults to the first item and is cleared when the list is empty
 * @summary Replace the product's media list and featured image
 */
export const putAdminV1OrgProductsIdMedia = (
    { id }: PutAdminV1OrgProductsIdMediaPathParameters,
    putAdminV1OrgProductsIdMediaBody: BodyType<PutAdminV1OrgProductsIdMediaBody>,
 options?: SecondParameter<typeof customInstance<PutAdminV1OrgProductsIdMedia200>>,) => {
      return customInstance<PutAdminV1OrgProductsIdMedia200>(
      {url: `/admin/v1/org/products/${id}/media`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1OrgProductsIdMediaBody
    },
      options);
    }
  /**
 * @summary Related products (upsell, cross-sell, matching, similar), grouped by type in display order
 */
export const getAdminV1OrgProductsIdRelations = (
    { id }: GetAdminV1OrgProductsIdRelationsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgProductsIdRelations200>>,) => {
      return customInstance<GetAdminV1OrgProductsIdRelations200>(
      {url: `/admin/v1/org/products/${id}/relations`, method: 'GET'
    },
      options);
    }
  /**
 * Relations are one-directional: a matching band listed on a ring does not list the ring. Order within a relation type is kept as given. A product cannot relate to itself; related products must belong to the organization and not be deleted
 * @summary Replace every relation of the product
 */
export const putAdminV1OrgProductsIdRelations = (
    { id }: PutAdminV1OrgProductsIdRelationsPathParameters,
    putAdminV1OrgProductsIdRelationsBody: BodyType<PutAdminV1OrgProductsIdRelationsBody>,
 options?: SecondParameter<typeof customInstance<PutAdminV1OrgProductsIdRelations200>>,) => {
      return customInstance<PutAdminV1OrgProductsIdRelations200>(
      {url: `/admin/v1/org/products/${id}/relations`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1OrgProductsIdRelationsBody
    },
      options);
    }
  export type GetAdminV1OrgProductsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProducts>>>
export type PostAdminV1OrgProductsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgProducts>>>
export type GetAdminV1OrgProductsHealthResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProductsHealth>>>
export type PostAdminV1OrgProductsLookupResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgProductsLookup>>>
export type GetAdminV1OrgProductsTagsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProductsTags>>>
export type GetAdminV1OrgProductsVendorsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProductsVendors>>>
export type GetAdminV1OrgProductsIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProductsId>>>
export type PatchAdminV1OrgProductsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgProductsId>>>
export type DeleteAdminV1OrgProductsIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgProductsId>>>
export type PostAdminV1OrgProductsIdDuplicateResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgProductsIdDuplicate>>>
export type PostAdminV1OrgProductsBulkResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgProductsBulk>>>
export type PatchAdminV1OrgProductsIdOptionsReorderResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgProductsIdOptionsReorder>>>
export type GetAdminV1OrgProductsIdSkuPreviewResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProductsIdSkuPreview>>>
export type PostAdminV1OrgProductsIdRegenerateSkusResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgProductsIdRegenerateSkus>>>
export type GetAdminV1OrgProductsIdSkuHistoryResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProductsIdSkuHistory>>>
export type GetAdminV1OrgProductsIdMediaResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProductsIdMedia>>>
export type PutAdminV1OrgProductsIdMediaResult = NonNullable<Awaited<ReturnType<typeof putAdminV1OrgProductsIdMedia>>>
export type GetAdminV1OrgProductsIdRelationsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProductsIdRelations>>>
export type PutAdminV1OrgProductsIdRelationsResult = NonNullable<Awaited<ReturnType<typeof putAdminV1OrgProductsIdRelations>>>
